import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import './form-edit.styles.scss';

import ErrorMessage from '../../components/error/error.component';
import Spinner from '../../components/spinner/spinner.component';
import GeneralConstants from '../../constants/general.constants';
import NotFound from '../not-found/not-found.component';

import api from '../../services/api.service';

class FormEdit extends React.Component {
    state = {
        formData: {},
        error: null,
        errorMessage: '',
        isFetching: true,
        sortField: null,
        sortDirection: 'asc',
        editingField: null,
        editingOption: null,
        newOption: { sys_name: '', name: '', pos: 0 },
        internalName: '',
        generatedSysName: '', // Add this new state property for tracking generated system name
        activeFieldForNewOption: null // Track which field is actively being filled out
    };

    async componentDidMount() {
        try {
            const formData = await api.formElement.get();
            this.setState({
                formData: formData,
                isFetching: false,
                error: false
            });
        } catch (error) {
            this.setState({
                isFetching: false,
                error,
                errorMessage: true
            });
        }
    }

    handleStartEditOption = (fieldName, option) => {
        // Store the initial values separately, including original values for comparison
        this.setState({
            editingField: fieldName,
            editingOption: { 
                ...option,
                originalName: option.name,
                originalSysName: option.sys_name,
                originalPos: option.pos // Store original position for comparison
            },
            internalName: option.name || '',
            // Keep the original system name unchanged during editing
            generatedSysName: option.sys_name || ''
        });
    };

    handleCancelEdit = () => {
        this.setState({
            editingField: null,
            editingOption: null,
            internalName: '',
            generatedSysName: ''
        });
    };

    handleNameInputChange = (e) => {
        // Only update the internal name state, don't auto-generate system name for editing
        const name = e.target.value;
        this.setState({ 
            internalName: name
            // Remove auto-generation of system name during editing
        });
    };

    generateSlug = (text) => {
        if (!text) return '';
        
        // Simple slug generation without special character handling
        return text
            .toLowerCase()
            .replace(/æ/g, 'ae')
            .replace(/ø/g, 'o')
            .replace(/å/g, 'a')
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-');
    };

    handlePosChange = (e) => {
        const options = this.state.formData[this.state.editingField] || [];
        const maxPos = this.getNextPosition(options);
        
        // Parse the input value and ensure it's within valid range (0 to maxPos)
        let pos = parseInt(e.target.value, 10) || 0;
        pos = Math.max(0, Math.min(pos, maxPos));
        
        this.setState(prevState => ({
            editingOption: {
                ...prevState.editingOption,
                pos: pos // Update with the valid position value
            }
        }));
    };

    update = async () => {
        try {
            const { formData, editingField, editingOption, internalName } = this.state;
            
            // Get the original position and new position
            const originalPos = editingOption.originalPos || editingOption.pos;
            const newPos = editingOption.pos;
            
            // Check if anything has actually changed by comparing with the original values
            const nameChanged = internalName !== editingOption.originalName;
            const posChanged = originalPos !== newPos;
            // Remove system name change detection since it should never change during editing
            
            // If nothing changed, just exit edit mode without updating
            if (!nameChanged && !posChanged) {
                this.setState({
                    editingField: null,
                    editingOption: null,
                    internalName: '',
                    generatedSysName: ''
                });
                return;
            }
            
            // Create updated option with the new values from state
            // Keep the original system name unchanged
            const updatedOption = {
                ...editingOption,
                name: internalName,
                sys_name: editingOption.originalSysName, // Always use the original system name
                pos: newPos
            };
            
            // Find all options in this field group
            let updatedOptions = [...formData[editingField]];
            
            // Use the original sys_name for filtering
            const originalSysName = editingOption.originalSysName || editingOption.sys_name;
            updatedOptions = updatedOptions.filter(opt => opt.sys_name !== originalSysName);
            
            // Adjust other positions of other options if the position has changed
            if (posChanged) {
                // Shift positions of other items
                updatedOptions = updatedOptions.map(opt => {
                    // Do not work perfectly either in front-end or back-end
                    // We have therefore chosen to only update the position of the edited option, for now
                    /*
                    if (opt.pos >= newPos) {
                        // Shift up items with position >= new position
                        return { ...opt, pos: opt.pos + 1 };
                    }
                    */
                    return opt;
                });
            }
            
            // Add the updated option back
            updatedOptions.push(updatedOption);
            
            // Sort the options by position
            updatedOptions.sort((a, b) => a.pos - b.pos);
            
            // API call to update the option
            const payload = {
                group: editingField,
                name: updatedOption.name,
                sys_name: originalSysName, // Use the original sys_name to identify the record
                new_sys_name: originalSysName, // This will be the same as original since we don't change it
                pos: updatedOption.pos // Make sure position is included in the payload
            };
            
            await api.formElement.put('', payload);
            
            this.setState({
                formData: {
                    ...this.state.formData,
                    [editingField]: updatedOptions
                },
                editingField: null,
                editingOption: null,
                internalName: '',
                generatedSysName: ''
            });
        } catch (error) {
            console.error('Error updating option:', error);
            this.setState({
                error,
                errorMessage: true
            });
        }
    };

    delete = async (fieldName, sys_name, pos) => {
        if (!window.confirm('Are you sure you want to delete this option?')) return;
        
        try {
            // Filter out the option to delete
            const updatedOptions = this.state.formData[fieldName].filter(opt => 
                opt.sys_name !== sys_name
            );
            
            // API call to update the option
            const payload = {
                group: fieldName,
                sys_name: sys_name,
                pos: pos
            };
            
            await api.formElement.delete('', payload);
            
            this.setState(prevState => ({
                formData: {
                    ...prevState.formData,
                    [fieldName]: updatedOptions
                }
            }));
        } catch (error) {
            this.setState({
                error,
                errorMessage: true
            });
        }
    };

    // For the new option, use simple direct state management
    handleNewName = (e, fieldName) => {
        const name = e.target.value;
        const sys_name = this.generateSlug(name);
        
        // If this is the first interaction with this field, set the position
        if (this.state.activeFieldForNewOption !== fieldName) {
            const options = this.state.formData[fieldName];
            const defaultPos = this.getNextPosition(options);
            
            this.setState({ 
                newOption: {
                    ...this.state.newOption,
                    name: name,
                    sys_name: sys_name,
                    pos: defaultPos // Set default position on first interaction
                },
                activeFieldForNewOption: fieldName
            });
        } else {
            this.setState({ 
                newOption: {
                    ...this.state.newOption,
                    name: name,
                    sys_name: sys_name
                },
                activeFieldForNewOption: fieldName
            });
        }
    };

    handleNewPos = (e, fieldName) => {
        const options = this.state.formData[fieldName];
        const maxPos = this.getNextPosition(options);
        
        // Ensure the position is within valid range (0 to maxPos)
        let pos = parseInt(e.target.value, 10) || 0;
        pos = Math.max(0, Math.min(pos, maxPos));
        
        this.setState({
            newOption: {
                ...this.state.newOption,
                pos
            },
            activeFieldForNewOption: fieldName
        });
    };

    create = async (fieldName) => {
        try {
            const { formData, newOption } = this.state;
            const options = formData[fieldName];
            const maxPos = this.getNextPosition(options);
            
            // Ensure we have a valid position
            // If newOption.pos is 0 or undefined, use maxPos instead
            const position = newOption.pos || maxPos;
            
            // Generate system name
            const sys_name = this.generateSlug(newOption.name);
            
            // Create new option with the position
            const optionToAdd = {
                name: newOption.name,
                sys_name: sys_name,
                pos: position
            };
            
            // Get all existing options and shift positions for those with pos >= new pos
            // Only if the new position is not at the end
            let updatedOptions = options.map(opt => {
                if (opt.pos >= position && position < maxPos) {
                    return { ...opt, pos: opt.pos + 1 };
                }
                return opt;
            });
            
            // Add the new option
            updatedOptions.push(optionToAdd);
            
            // Sort by position
            updatedOptions.sort((a, b) => a.pos - b.pos);
            
            // API call to update the option
            const payload = {
                group: fieldName,
                name: newOption.name,
                sys_name: newOption.sys_name,
                pos: position
            };
            
            await api.formElement.post(payload);
            
            this.setState({
                formData: {
                    ...this.state.formData,
                    [fieldName]: updatedOptions
                },
                newOption: { sys_name: '', name: '', pos: 0 },
                activeFieldForNewOption: null
            });
        } catch (error) {
            this.setState({
                error,
                errorMessage: true
            });
        }
    };

    getNextPosition = (options) => {
        return Math.max(...options.map(opt => opt.pos), 0) + 1;
    };

    // Initialize the newOption with the correct default position when focusing a field
    handleFieldFocus = (fieldName) => {
        const options = this.state.formData[fieldName];
        const defaultPos = this.getNextPosition(options);
        
        this.setState({
            activeFieldForNewOption: fieldName,
            newOption: {
                ...this.state.newOption,
                pos: defaultPos // Set the default position when focusing
            }
        });
    };

    render() {
        // Add activeFieldForNewOption to destructured state variables
        const { 
            isFetching, errorMessage, formData, editingField, 
            editingOption, newOption, internalName, generatedSysName,
            activeFieldForNewOption
        } = this.state;
        
        return (
            <div className='project-data-container'>
                {isFetching && (
                    <CSSTransition in={isFetching} appear timeout={{ appear: 300, exit: 150 }}>
                        <Spinner />
                    </CSSTransition>
                )}
                
                {this.state.error?.http_code === 404 ? (
                    <NotFound />
                ) : (
                    errorMessage && (
                        <ErrorMessage errorID={this.state.error?.id || this.state.error?.status} />
                    )
                )}

                <div className='title-container'>
                    <h1>{GeneralConstants['edit_form_data']}</h1>
                </div>

                <nav className='project-nav'>
                    <NavLink to='/' className='project-nav--home' />
                    <NavLink to='/admin-user' className='admin-nav--user' activeClassName='admin-nav--active'>
                        {GeneralConstants['edit_users']}
                    </NavLink>
                    <NavLink to='/admin-form' className='admin-nav--form' activeClassName='admin-nav--active'>
                        {GeneralConstants['edit_form_data']}
                    </NavLink>
                </nav>

                <div className='project-form-container'>
                    {Object.keys(formData).length > 0 ? 
                        Object.entries(formData).map(([fieldName, options]) => (
                            <div key={fieldName} className="field-container">
                                <h2>{GeneralConstants[fieldName]}</h2>
                                <table className="options-table">
                                    <thead>
                                        <tr>
                                            <th>Tittel</th>
                                            <th>Systemnavn</th>
                                            <th>Posisjon</th>
                                            <th>Handling</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {options.map(option => (
                                            <tr key={option.sys_name}>
                                                {editingField === fieldName && editingOption?.sys_name === option.sys_name ? (
                                                    // Edit mode
                                                    <>
                                                        <td>
                                                            <input 
                                                                type="text" 
                                                                value={internalName} 
                                                                onChange={this.handleNameInputChange} 
                                                            />
                                                        </td>
                                                        <td>
                                                            <input 
                                                                type="text" 
                                                                value={generatedSysName} 
                                                                disabled
                                                                className="disabled-input"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input 
                                                                type="number" 
                                                                min="0"
                                                                max={this.getNextPosition(options)}
                                                                value={editingOption?.pos || 0} 
                                                                onChange={this.handlePosChange} 
                                                                onBlur={(e) => {
                                                                    // Ensure we have a valid position on blur
                                                                    const maxPos = this.getNextPosition(options);
                                                                    const pos = parseInt(e.target.value, 10) || 0;
                                                                    const validPos = Math.max(0, Math.min(pos, maxPos));
                                                                    if (pos !== validPos) {
                                                                        this.handlePosChange({ target: { value: validPos } });
                                                                    }
                                                                }} 
                                                            />
                                                        </td>
                                                        <td className="action-buttons">
                                                            <button 
                                                                className="btn btn-success"
                                                                onClick={this.update}
                                                            >
                                                                Lagre
                                                            </button>
                                                            <button 
                                                                className="btn btn-secondary"
                                                                onClick={this.handleCancelEdit}
                                                            >
                                                                Avbryt
                                                            </button>
                                                        </td>
                                                    </>
                                                ) : (
                                                    // Display mode
                                                    <>
                                                        <td>{option.name}</td>
                                                        <td>{option.sys_name}</td>
                                                        <td>{option.pos}</td>
                                                        <td className="action-buttons">
                                                            <button 
                                                                className="btn btn-primary"
                                                                onClick={() => this.handleStartEditOption(fieldName, option)}
                                                            >
                                                                Endre
                                                            </button>
                                                            <button 
                                                                className="btn btn-danger"
                                                                onClick={() => this.delete(fieldName, option.sys_name, option.pos)}
                                                            >
                                                                Slett
                                                            </button>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                        <tr className="add-option-row">
                                            <td>
                                                <input 
                                                    type="text" 
                                                    placeholder="Tittel" 
                                                    value={activeFieldForNewOption === fieldName ? newOption.name : ''}
                                                    onChange={(e) => this.handleNewName(e, fieldName)}
                                                    onFocus={() => this.handleFieldFocus(fieldName)}
                                                />
                                            </td>
                                            <td>
                                                <input 
                                                    type="text" 
                                                    placeholder="Systemnavn" 
                                                    value={activeFieldForNewOption === fieldName ? newOption.sys_name : ''}
                                                    disabled
                                                    className="disabled-input"
                                                />
                                            </td>
                                            <td>
                                                <input 
                                                    type="number" 
                                                    placeholder={`${this.getNextPosition(options)}`}
                                                    min="0"
                                                    max={this.getNextPosition(options)}
                                                    value={activeFieldForNewOption === fieldName ? (newOption.pos || '') : ''}
                                                    onChange={(e) => this.handleNewPos(e, fieldName)}
                                                    onFocus={() => this.handleFieldFocus(fieldName)}
                                                />
                                            </td>
                                            <td>
                                                <button 
                                                    className="btn btn-success"
                                                    onClick={() => this.create(fieldName)}
                                                    disabled={activeFieldForNewOption !== fieldName || !newOption.sys_name || !newOption.name}
                                                >
                                                    Legg til
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )) : (
                            <div className='form-placeholder'>
                                <p>No form data available</p>
                            </div>
                        )
                    }
                </div>
            </div>
        );
    }
}

export default FormEdit;
